// src\components\common\AlertNotification.jsx
import React, { useState, useEffect } from 'react';
import { X, AlertTriangle, CheckCircle, AlertCircle, Info } from 'lucide-react';

/**
 * Composant pour afficher des alertes et notifications
 * @param {Object} props - Propriétés du composant
 * @param {string} props.type - Type d'alerte ('success', 'error', 'warning', 'info')
 * @param {string} props.message - Message à afficher
 * @param {number} props.duration - Durée d'affichage en ms (0 pour ne pas disparaître)
 * @param {Function} props.onClose - Fonction appelée à la fermeture
 */
const AlertNotification = ({ type = 'info', message, duration = 5000, onClose }) => {
  const [visible, setVisible] = useState(true);
  
  // Configuration des styles selon le type
  const getConfig = () => {
    switch (type) {
      case 'success':
        return {
          bgColor: 'bg-green-100',
          borderColor: 'border-green-500',
          textColor: 'text-green-700',
          icon: <CheckCircle className="h-5 w-5 text-green-500" />
        };
      case 'error':
        return {
          bgColor: 'bg-red-100',
          borderColor: 'border-red-500',
          textColor: 'text-red-700',
          icon: <AlertTriangle className="h-5 w-5 text-red-500" />
        };
      case 'warning':
        return {
          bgColor: 'bg-yellow-100',
          borderColor: 'border-yellow-500',
          textColor: 'text-yellow-700',
          icon: <AlertCircle className="h-5 w-5 text-yellow-500" />
        };
      case 'info':
      default:
        return {
          bgColor: 'bg-blue-100',
          borderColor: 'border-blue-500',
          textColor: 'text-blue-700',
          icon: <Info className="h-5 w-5 text-blue-500" />
        };
    }
  };
  
  // Fermer l'alerte
  const handleClose = () => {
    setVisible(false);
    if (onClose) onClose();
  };
  
  // Si la durée est spécifiée, fermer automatiquement
  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      
      return () => clearTimeout(timer);
    }
  }, [duration]);
  
  // Ne pas rendre si non visible
  if (!visible) return null;
  
  const config = getConfig();
  
  return (
    <div className={`fixed bottom-4 right-4 ${config.bgColor} border-l-4 ${config.borderColor} ${config.textColor} p-4 rounded shadow-lg max-w-md animate-fade-in z-50`}>
      <div className="flex">
        <div className="flex-shrink-0">
          {config.icon}
        </div>
        <div className="ml-3 mr-8 flex-grow">
          <p className="text-sm">{message}</p>
        </div>
        <button 
          className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
          onClick={handleClose}
        >
          <X size={18} />
        </button>
      </div>
      
      {/* Barre de progression pour le délai d'expiration */}
      {duration > 0 && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-200">
          <div 
            className={`h-1 transition-all duration-${duration}ms ease-linear ${
              type === 'success' ? 'bg-green-500' :
              type === 'error' ? 'bg-red-500' :
              type === 'warning' ? 'bg-yellow-500' :
              'bg-blue-500'
            }`}
            style={{ 
              width: '100%',
              animation: `shrink ${duration}ms linear forwards`
            }}
          ></div>
        </div>
      )}
      
      <style jsx="true">{`
        @keyframes shrink {
          from { width: 100%; }
          to { width: 0%; }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fade-in {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default AlertNotification;